/**
 * VRG-306: what KIND of company this is.
 *
 * Deliberately an enum rather than another boolean alongside `is_example` /
 * `is_test` — the kinds are mutually exclusive by construction, and a fourth
 * flavour extends cleanly here where a fourth boolean would not.
 *
 * ORTHOGONAL to `is_example`. `kind` says what a company IS; `is_example` says
 * "exclude from anything that counts or charges". A demo company sets BOTH. The
 * VRG-280 monitoring canary sets only `is_example` and still issues a REAL
 * certificate. Never infer one from the other.
 */
export enum CompanyKind {
  /** A real customer company. The default. */
  Standard = 'standard',

  /**
   * An acquisition demo provisioned by Vera from a prospect's public website,
   * for cold outbound (VRG-305). Its imagery is scraped and not ours, it lives
   * at `<brand>-demo.app.vera.inc`, and every asset is purgeable in one action.
   *
   * Note this does NOT mean "cannot verify". A Tier-1 demo ships a real,
   * Vera-encoded chip, so a tap cryptographically verifies — and that is
   * correct. The false claim on a demo page is provenance, not cryptography,
   * and the non-dismissible demo banner is what addresses it.
   */
  Demo = 'demo',

  /**
   * VRG-378: Vera itself. Exactly one row exists, enforced by a partial unique
   * index (`companies_single_internal_idx`), and it is created by migration
   * rather than by a seeder so that every environment has it after a deploy
   * rather than after someone remembers to re-run a standup job.
   *
   * It exists so that rows belonging to Vera rather than to a brand have a
   * company to point at. `support_requests.company_id` is `NOT NULL` by
   * deliberate design — "the conversation belongs to the company, not to the
   * individual who opened it" — so before this, a Vera staffer with no company
   * membership could not file a support request at all; the request 403'd in the
   * auth guard before it reached the handler. Giving Vera a row keeps that
   * invariant intact instead of making the column nullable, and hands the
   * internal queue a clean predicate for keeping staff-authored reports out of
   * the brand-facing queue.
   *
   * Also `is_example = true`, for the same reason a demo is: Vera must not meter
   * or bill itself, and its own rows must not land in brand-facing aggregates.
   * As always, do not infer either flag from the other.
   */
  Internal = 'internal',
}
